/**
 * Socrative Student Quiz Navigation
 *
 * This component provides the following features:
 *
 *     1. Text for the current question and total number of questions (e.g. 1 of 12).
 *     2. First and last buttons for jumping to the first/last questions of a quiz.
 *          - These buttons are hidden on mobile.
 *     3. Previous and next buttons for moving back and forth one question at a time.
 *     4. Up to 9 question number buttons for jumping directly to specific questions.
 *          - On mobile, only 5 question number buttons are visible
 *
 * When a question jump occurs, the target question number is centered (if there are enough
 * questions before and after). Here are examples after jumping on desktop/mobile:
 *
 * Desktop: Jump from question 1 to 9, when there are a total of 15 questions:
 *
 *                  9 of 15
 *     [<<][<] 6 7 8 [9] 10 11 12 [>][>>]
 *
 * Mobile: Jump from question 1 to 5, when there are a total of 15 questions:
 *
 *           5 of 15
 *     [<] 3 4 [5] 6 7 [>]
 *
 * Properties:
 *
 * currentQuestion {number}   The question number currently being displayed
 * nextClicked     {function} Callback invoked when the next button is clicked
 * questionClicked {function} Callback invoked when any question number is clicked
 * navProps        {object}   An object with the type and state of each question
 * previousClicked {function} Callback invoked when the previous button is clicked
 * totalQuestions  {number}   The total number of questions to be navigated
 *
 * This component is used by the student quiz view during an open navigation quiz.
 */

import * as React from 'react';

import { translate } from '~l10n/client';

import { useMedia } from '~shared/hooks';
import FirstPageIcon from 'FirstPageIcon';
import LastPageIcon from 'LastPageIcon';
import NextArrowIcon from 'NextArrowIcon';
import PreviousArrowIcon from 'PreviousArrowIcon';

export default function QuizNav(props) {
  const isBigScreen = useMedia(['(min-width: 768px)'], [true], false);
  return <QuizNavInner {...props} isBigScreen={isBigScreen} />;
}

export class QuizNavInner extends React.Component {
  constructor(props) {
    super(props);
  }

  getQuestionsToDisplay() {
    const { currentQuestion, totalQuestions, isBigScreen } = this.props;
    let minCenteredQuestion = !isBigScreen ? 3 : 5;
    let maxQuestions = !isBigScreen ? 5 : 9;
    let questionPadding = !isBigScreen ? 2 : 4;
    let questionsToDisplay = [];
    let i = 1;

    if (
      currentQuestion <= minCenteredQuestion ||
      totalQuestions <= maxQuestions
    ) {
      for (; i <= Math.min(maxQuestions, totalQuestions); i++) {
        questionsToDisplay.push(i);
      }
    } else {
      let questionCount = 0;

      for (
        i = currentQuestion + questionPadding;
        questionCount < maxQuestions;
        i--
      ) {
        if (i <= totalQuestions) {
          questionsToDisplay.push(i);
          questionCount++;
        }
      }

      questionsToDisplay.reverse();
    }

    return questionsToDisplay;
  }

  canClickPrevious() {
    return this.props.currentQuestion > 1;
  }

  canClickNext() {
    const { currentQuestion, totalQuestions } = this.props;
    return currentQuestion < totalQuestions;
  }

  render() {
    const {
      className,
      currentQuestion,
      navProps,
      nextClicked,
      previousClicked,
      questionClicked,
      totalQuestions,
      isBigScreen,
    } = this.props;
    let buttonsToDisplay = [];

    if (isBigScreen) {
      buttonsToDisplay.push(
        <button
          key="buttonsToDisplay-button-1"
          className={
            'quiz-nav-button first-question-button' +
            (this.canClickPrevious() ? '' : ' disabled')
          }
          onClick={() => {
            if (questionClicked && this.canClickPrevious()) {
              questionClicked(1);
            }
          }}
        >
          <FirstPageIcon className="first-question-arrow" />
        </button>
      );
    }

    buttonsToDisplay.push(
      <button
        key="buttonsToDisplay-button-2"
        className={
          'quiz-nav-button previous-question-button' +
          (this.canClickPrevious() ? '' : ' disabled')
        }
        onClick={() => {
          if (previousClicked && this.canClickPrevious()) {
            previousClicked();
          }
        }}
      >
        <PreviousArrowIcon className="previous-question-arrow" />
      </button>
    );

    let questionNumbers = this.getQuestionsToDisplay();

    for (let i = 0; i < questionNumbers.length; i++) {
      let questionNumber = questionNumbers[i];
      let type = navProps[questionNumber - 1].type.toLowerCase();
      let selected = navProps[questionNumber - 1].selected;
      let answered = navProps[questionNumber - 1].answered === true;

      let buttonClass = 'quiz-nav-button question-number-button';
      buttonClass += answered
        ? ' ' + type + '-question-answered'
        : ' ' + type + '-question-unanswered';
      buttonClass += selected
        ? ' ' + type + '-question-selected'
        : ' ' + type + '-question-unselected';

      buttonsToDisplay.push(
        <div
          key={'question-number-container-' + questionNumber}
          className={
            'question-number-container' +
            (selected ? ' ' + type + '-container-selected' : '')
          }
        >
          <button
            className={buttonClass}
            key={'question-number-' + questionNumber}
            // Capture the question number in the closure.
            onClick={((questionNumber) => {
              return () => {
                if (questionClicked && questionNumber !== currentQuestion) {
                  questionClicked(questionNumber);
                }
              };
            })(questionNumber)}
          >
            {questionNumber}
          </button>
        </div>
      );
    }

    buttonsToDisplay.push(
      <button
        key="buttonsToDisplay-button-3"
        className={
          'quiz-nav-button next-question-button' +
          (this.canClickNext() ? '' : ' disabled')
        }
        onClick={() => {
          if (nextClicked && this.canClickNext()) {
            nextClicked();
          }
        }}
      >
        <NextArrowIcon className="next-question-arrow" />
      </button>
    );

    if (isBigScreen) {
      buttonsToDisplay.push(
        <button
          key="buttonsToDisplay-div-4"
          className={
            'quiz-nav-button last-question-button' +
            (this.canClickNext() ? '' : ' disabled')
          }
          onClick={() => {
            if (questionClicked && this.canClickNext()) {
              questionClicked(totalQuestions);
            }
          }}
        >
          <LastPageIcon className="last-question-arrow" />
        </button>
      );
    }

    return (
      <div
        id="student-nav-container"
        className={'student-nav-container' + (className ? ' ' + className : '')}
      >
        <div className="student-nav-x-of-x-container">
          <span
            className="question-range"
            dangerouslySetInnerHTML={{
              __html: '<b>{0}</b> <i>{1}</i> <b>{2}</b>'.format(
                currentQuestion,
                translate('of'),
                totalQuestions
              ),
            }}
          />
        </div>
        <div className="nav-buttons-container">{buttonsToDisplay}</div>
      </div>
    );
  }
}
